'use client';

import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { usePriceAlertsCount } from '@/lib/api/priceAlerts';
import { useAuthSession } from '@/lib/hooks/useAuth';
import type { Market, MarketItem } from '@/lib/types/markets/market';
import { AnimatedIcon } from '@/lib/ui-kit/animated-icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/lib/ui-kit/tooltip';
import { formatBadgeCount } from '@/lib/utils/formatBadgeCount';
import { isMarketEnded } from '@/lib/utils/outcomePrice';
import { PriceAlertsDialog } from './PriceAlertsDialog';
import { isOutcomeTradable } from './utils';

interface PriceAlertTriggerButtonProps {
  market: Market;
  outcomes: MarketItem[];
  defaultOutcomeId?: string;
}

export function PriceAlertTriggerButton({
  market,
  outcomes,
  defaultOutcomeId,
}: PriceAlertTriggerButtonProps) {
  const t = useTranslations('priceAlerts');
  const [open, setOpen] = useState(false);
  const { isAuthenticated } = useAuthSession();
  const { data: alertsCount = 0 } = usePriceAlertsCount({ enabled: isAuthenticated });

  const tradableOutcomes = useMemo(() => outcomes.filter(isOutcomeTradable), [outcomes]);

  if (!isAuthenticated || isMarketEnded(market) || tradableOutcomes.length === 0) {
    return null;
  }

  const resolvedDefaultOutcomeId =
    defaultOutcomeId && tradableOutcomes.some((o) => o.id === defaultOutcomeId)
      ? defaultOutcomeId
      : undefined;

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-label={t('title')}
            className="relative flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-[8px] p-2 text-v2-icon-primary outline-none transition-colors hover:bg-v2-btn-secondary hover:text-v2-icon-primary-hover"
          >
            <AnimatedIcon name="price-alert" size={24} className="lottie-current" />
            {alertsCount > 0 && (
              <span
                aria-hidden
                className={`pointer-events-none absolute top-[4px] right-[4px] flex h-[16px] items-center justify-center rounded-full bg-v2-btn-premium-red-light font-medium font-sans text-[10px] leading-none text-white ${
                  alertsCount > 99 ? 'min-w-[22px] px-[5px]' : 'w-[16px]'
                }`}
              >
                {formatBadgeCount(alertsCount)}
              </span>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          sideOffset={6}
          showArrow={false}
          className="rounded-[10px] border border-v2-border bg-v2-btn-secondary-hover px-3 py-2 text-[12px] leading-4 font-normal text-v2-text-primary shadow-[1px_1px_8px_0_rgba(0,0,0,0.2)] tracking-[-0.01em] dark:border-0 dark:bg-v2-surface-tertiary"
        >
          {t('tooltip')}
        </TooltipContent>
      </Tooltip>
      <PriceAlertsDialog
        open={open}
        onOpenChange={setOpen}
        market={market}
        outcomes={tradableOutcomes}
        defaultOutcomeId={resolvedDefaultOutcomeId}
      />
    </>
  );
}
